/**
 * The one-line headline for a workout in the history list.
 *
 * It used to be tonnage, unconditionally — `formatWeight(volumeKg)` over every
 * working set. That is the right number for a session of squats and the wrong
 * one for about a third of what this library can log, because volume is
 * weight × reps and a great deal of training has neither. A 5 km run summarised
 * as "0 lb" is not a rounding problem: it is the app reporting that a workout
 * you did contained no work.
 *
 * So the headline says what the session actually measured. The order is by how
 * much a number tells you about the session it came from — tonnage first where
 * there is any, then distance, then time, and reps only when nothing else
 * survived, which is exactly the bodyweight-circuit case that would otherwise
 * still read as zero.
 *
 * Two parts at most. Three fits on a phone in the sense that it does not wrap,
 * and not in the sense that anybody reads it; the history list is scanned, and
 * a row that has to be parsed is a row that gets scrolled past.
 */

// `.ts` on purpose: node --experimental-strip-types resolves relative imports
// literally, so a tested module cannot lean on Vite's extension guessing. Same
// reason progression.ts and prefill.ts spell it out.
import { formatDistance, formatDuration, formatWeight,
         type DistanceUnit, type Unit } from './units.ts'

export type SessionTotals = {
  /** Working-set tonnage, kg. Zero for anything not counted in weight × reps. */
  volumeKg: number
  /** Metres, summed across every distance set in the session. */
  distanceM: number
  durationSeconds: number
  reps: number
}

/**
 * "12,400 lb", "5 km · 25:00", "2:15", "45 reps", or "—".
 *
 * `du` is the unit the distance half is written in. The caller picks it from
 * the session's own exercises rather than from one exercise's override — see
 * summarizeSessions, where a session that mixes an erg with a road run has to
 * settle on one.
 */
export function summaryLine(
  totals: SessionTotals,
  opts: { unit: Unit; du: DistanceUnit },
): string {
  const parts: string[] = []

  if (totals.volumeKg > 0) parts.push(formatWeight(totals.volumeKg, opts.unit))
  if (totals.distanceM > 0) parts.push(formatDistance(totals.distanceM, opts.du))
  if (parts.length < 2 && totals.durationSeconds > 0) {
    parts.push(formatDuration(totals.durationSeconds))
  }
  // Only when nothing above survived. A set of chin ups already contributes
  // tonnage through its bodyweight factor, so reps here means the exercises
  // that carry no load at all — push ups, crunches, the ones deliberately left
  // at factor 0. Their rep count is the only number the session produced.
  if (parts.length === 0 && totals.reps > 0) {
    parts.push(`${totals.reps} rep${totals.reps === 1 ? '' : 's'}`)
  }

  // An em dash, not "0 lb". A session with nothing measurable in it is a real
  // thing — an empty one you started and abandoned — and saying so plainly
  // beats inventing a zero for it.
  return parts.slice(0, 2).join(' · ') || '—'
}
